import { ApplicationView } from '@aha-app/mvc';
import classNames from 'classnames';
import React, { useState } from 'react';
import { type IntegrationRecordField } from '../../shared-types';
import customElementBoolAttr from '../../utils/customElementBoolAttr';
import styles from './create-records.module.css';
import type { IntegrationRecordsModalController } from './IntegrationRecordsModalController';
import SelectField from '../../components/common/SelectField';
import TextField from '../../components/common/TextField';

interface FormRowProps {
  label?: string;
  required?: boolean;
  children: React.ReactNode;
}

export const FormRow: React.FC<FormRowProps> = ({
  label,
  required,
  children,
}) => (
  <div
    className={classNames(styles.formRow, {
      [styles.required]: required,
    })}
  >
    {label ? (
      <label className={styles.formRowLabel}>{label}</label>
    ) : (
      <span className={styles.spacer} />
    )}
    <div className={styles.formRowContent}>{children}</div>
  </div>
);

// coerces unknown input to a comma-separated string
const coerceArrayInput = (value: unknown) =>
  Array.isArray(value) ? value.join(',') : String(value ?? '');

interface FieldProps {
  field: IntegrationRecordField;
  controller: IntegrationRecordsModalController;
}

export const Field: React.FC<FieldProps> = ApplicationView(props => {
  const { field, controller } = props;
  const { fieldData } = controller.state;
  switch (field.type.type) {
    case 'text':
    case 'number':
    case 'date':
    case 'datetime':
      return (
        <TextField
          value={String(fieldData[field.name] ?? '')}
          setValue={value => controller.actionUpdateField(field.name, value)}
          fieldType={field.type.type}
        />
      );
    case 'select': {
      const { options, multiple } = field.type;
      return (
        <SelectField
          setValue={value => {
            controller.actionUpdateField(
              field.name,
              multiple ? value.map(v => v.value) : value.value
            );
          }}
          initialValue={fieldData[field.name] as any}
          getOptions={async ({ searchParam, page }) => {
            if (typeof options === 'function') {
              return await options({ searchParam, page });
            } else {
              return {
                options: options.map(opt => ({
                  value: opt.value,
                  text: opt.text,
                  item: opt.value,
                })),
              };
            }
          }}
          multiple={multiple}
        />
      );
    }

    case 'tags':
      return (
        <TextField
          value={coerceArrayInput(fieldData[field.name])}
          setValue={value =>
            controller.actionUpdateField(
              field.name,
              value.split(',').map(s => s.trim())
            )
          }
        />
      );
    default: {
      const message = `Unknown field type ${
        // @ts-expect-error handling an unknown type here
        field.type.type
      }`;
      console.warn(message, field);
      if (process.env.NODE_ENV !== 'production') {
        throw new Error(message);
      }
      return null;
    }
  }
});

interface SummaryFieldContentProps {
  shapeId: string;
  controller: IntegrationRecordsModalController;
}
export const SummaryField: React.FC<SummaryFieldContentProps> = ApplicationView(
  ({ shapeId, controller }) => {
    const [showDescription, setShowDescription] = useState(false);

    const shape = controller.state.records[shapeId] ?? {};

    return (
      <>
        <TextField
          value={shape.summary}
          setValue={value => {
            controller.actionUpdateSummary(shapeId, value);
          }}
        />

        {showDescription || shape.description ? (
          <TextField
            placeholder='Description'
            value={shape.description ?? ''}
            setValue={value => {
              controller.actionUpdateDescription(shapeId, value);
            }}
          />
        ) : (
          <div>
            <aha-button kind='link' onClick={() => setShowDescription(true)}>
              Add description
            </aha-button>
          </div>
        )}
      </>
    );
  }
);

interface ButtonProps {
  onClick?: () => void;
  disabled?: boolean;
  primary?: boolean;
  type?: 'submit';
  children: React.ReactNode;
}

export const Button: React.FC<ButtonProps> = ({
  children,
  onClick,
  disabled,
  type,
  primary,
}) => (
  <aha-button
    kind={primary ? 'primary' : 'secondary'}
    slot={primary ? undefined : 'secondary_actions'}
    type={type ?? 'button'}
    disabled={customElementBoolAttr(disabled ?? false)}
    onClick={() => (!disabled && onClick ? onClick() : undefined)}
  >
    {children}
  </aha-button>
);
